/**
 * Shared utility functions used across thumbnail editor modules
 */

import { GRAPHICS_IMAGE_DEFAULT_ASPECT_RATIOS } from "constants/graphics";
import { FILL_COLOR, FONT_SIZE, FONT_WEIGHT } from "constants/ThumbnailEditor";
import { fabric } from "fabric";
import { v4 as uuid } from "uuid";

import { findObjectByPath } from "./form-structure";

/**
 * Transform text objects recursively
 * @param {Array} objects - Array of fabric objects
 */
export function transformText(objects) {
  if (!objects) return;

  objects.forEach((item) => {
    if (item.objects) {
      transformText(item.objects);
    } else {
      return item.type === "text" || item.type === "textbox";
    }
  });
}

/**
 * Download file with generated UUID name
 * @param {string} file - File data URL
 * @param {string} type - File extension
 */
export function downloadFile(file, type) {
  const anchorElement = document.createElement("a");

  anchorElement.href = file;
  anchorElement.download = `${uuid()}.${type}`;
  document.body.appendChild(anchorElement);
  anchorElement.click();
  anchorElement.remove();
}

/**
 * Check if type is a text-based fabric object
 * @param {string} type - Fabric object type
 * @returns {boolean}
 */
export function isTextType(type) {
  return type === "text" || type === "i-text" || type === "textbox";
}

/**
 * Convert RGBA object to string format
 * @param {Object} rgba - RGBA color object
 * @returns {string} RGBA string
 */
export function rgbaObjectToString(rgba) {
  if (rgba === "transparent") {
    return `rgba(0,0,0,0)`;
  }

  const alpha = rgba.a === undefined ? 1 : rgba.a;

  return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${alpha})`;
}

/**
 * Convert RGBA string to hex color
 * @param {string} rgba - RGBA color string
 * @returns {string} Hex color string
 */
export const rgbaToHex = (rgba) => {
  if (typeof rgba !== "string") {
    return rgba;
  }

  const result = rgba.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/);

  if (!result) {
    return rgba;
  }

  const r = parseInt(result[1], 10);
  const g = parseInt(result[2], 10);
  const b = parseInt(result[3], 10);

  if (isNaN(r) || isNaN(g) || isNaN(b) || r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
    return rgba;
  }

  const componentToHex = (c) => {
    const hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  const hexR = componentToHex(r);
  const hexG = componentToHex(g);
  const hexB = componentToHex(b);

  return `#${hexR}${hexG}${hexB}`.toUpperCase();
};

/**
 * Create fabric.js filter based on filter name
 * @param {string} value - Filter name
 * @returns {Object|null} Fabric filter instance
 */
export const createFilter = (value) => {
  let effect;

  switch (value) {
    case "greyscale":
      effect = new fabric.Image.filters.Grayscale();
      break;
    case "polaroid":
      effect = new fabric.Image.filters.Polaroid();
      break;
    case "sepia":
      effect = new fabric.Image.filters.Sepia();
      break;
    case "kodachrome":
      effect = new fabric.Image.filters.Kodachrome();
      break;
    case "contrast":
      effect = new fabric.Image.filters.Contrast({ contrast: 0.3 });
      break;
    case "brightness":
      effect = new fabric.Image.filters.Brightness({ brightness: 0.8 });
      break;
    case "brownie":
      effect = new fabric.Image.filters.Brownie();
      break;
    case "vintage":
      effect = new fabric.Image.filters.Vintage();
      break;
    case "technicolor":
      effect = new fabric.Image.filters.Technicolor();
      break;
    case "pixelate":
      effect = new fabric.Image.filters.Pixelate();
      break;
    case "invert":
      effect = new fabric.Image.filters.Invert();
      break;
    case "blur":
      effect = new fabric.Image.filters.Blur();
      break;
    case "sharpen":
      effect = new fabric.Image.filters.Convolute({
        matrix: [0, -1, 0, -1, 5, -1, 0, -1, 0],
      });
      break;
    case "emboss":
      effect = new fabric.Image.filters.Convolute({
        matrix: [1, 1, 1, 1, 0.7, -1, -1, -1, -1],
      });
      break;
    case "removecolor":
      effect = new fabric.Image.filters.RemoveColor({
        threshold: 0.2,
        distance: 0.5,
      });
      break;
    case "blacknwhite":
      effect = new fabric.Image.filters.BlackWhite();
      break;
    case "vibrance":
      effect = new fabric.Image.filters.Vibrance({
        vibrance: 1,
      });
      break;
    case "blendcolor":
      effect = new fabric.Image.filters.BlendColor({
        color: "#00ff00",
        mode: "multiply",
      });
      break;
    case "huerotate":
      effect = new fabric.Image.filters.HueRotation({
        rotation: 0.5,
      });
      break;
    case "resize":
      effect = new fabric.Image.filters.Resize();
      break;
    case "gamma":
      effect = new fabric.Image.filters.Gamma({
        gamma: [1, 0.5, 2.1],
      });
      break;
    case "saturation":
      effect = new fabric.Image.filters.Saturation({
        saturation: 0.7,
      });
      break;
    default:
      effect = null;
      return;
  }

  return effect;
};

/**
 * Get current properties from editor instance
 * @param {Object} editor - Editor instance
 * @returns {Object} Editor properties
 */
export const getPropertiesFromEditor = (editor) => {
  return {
    fillColor: editor?.getActiveFillColor() || FILL_COLOR,
    strokeColor: editor?.getActiveStrokeColor(),
    fontFamily: editor?.getActiveFontFamily(),
    fontWeight: editor?.getActiveFontWeight() || FONT_WEIGHT,
    fontStyle: editor?.getActiveFontStyle() || "normal",
    fontLinethrough: editor?.getActiveFontLinethrough() || false,
    fontUnderline: editor?.getActiveFontUnderline() || false,
    textAlign: editor?.getActiveTextAlign() || "left",
    fontSize: editor?.getActiveFontSize() || FONT_SIZE,
    templatePayloadKey: editor?.getTemplatePayloadKey() || "",
    templatePayload: editor?.getTemplatePayload() || "",
    imageSrc: editor?.getImageSrc() || "",
    nameType: editor?.getTemplatePayload()?.nameType || "",
    textCase: editor?.getTemplatePayload()?.textCase || "default",
    textWrap: editor?.getTemplatePayload()?.textWrap ?? true,
    maxRows: editor?.getTemplatePayload()?.maxRows || 1,
    maxCols: editor?.getTemplatePayload()?.maxCols || 1,
    spacing: editor?.getTemplatePayload()?.spacing || 10,
    textVerticalCenterAlign: editor?.getTemplatePayload()?.textVerticalCenterAlign || false,
    alignment: editor?.getTemplatePayload()?.alignment || "center",
    autoLayout: editor?.getTemplatePayload()?.autoLayout || false,
    gameGroup: editor?.getTemplatePayload()?.gameGroup || "",
    horizontalSpacing: editor?.getTemplatePayload()?.horizontalSpacing || 10,
    verticalSpacing: editor?.getTemplatePayload()?.verticalSpacing || 10,
    aspectRatio:
      editor?.getTemplatePayload()?.aspectRatio ||
      GRAPHICS_IMAGE_DEFAULT_ASPECT_RATIOS[editor?.getTemplatePayload()?.type] ||
      "1:1",
  };
};

/**
 * Convert Data URL to File object
 * @param {string} dataurl - Data URL string
 * @param {string} filename - Desired filename
 * @returns {File|null} File object or null
 */
export const dataURLtoFile = (dataurl, filename) => {
  if (!dataurl) {
    console.error("dataURLtoFile: Received null or undefined dataurl.");
    return null;
  }

  try {
    const match = dataurl.match(/^data:(.+);base64,(.+)$/);
    if (!match) {
      console.error("dataURLtoFile: Invalid Data URL format.", dataurl.substring(0, 50) + "...");
      return null;
    }

    const mime = match[1];
    const base64Data = match[2];

    const bstr = atob(base64Data);

    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    const blob = new Blob([u8arr], { type: mime });
    const file = new File([blob], filename, { type: mime });

    return file;
  } catch (error) {
    console.error("Error converting Data URL to File:", error);
    console.error("Data URL (start):", dataurl.substring(0, 50) + "...");
    return null;
  }
};

/**
 * Get image URL array filtered by type
 * @param {Array} images - Array of image objects
 * @param {string} type - Image type to filter
 * @returns {Array} Array of image URLs
 */
export const getImageUrlArray = (images = [], type) => {
  return images?.filter((image) => image?.type === type)?.map((image) => image?.url) || [];
};

/**
 * Wait for next canvas render cycle
 * @returns {Promise<void>}
 */
export const waitForCanvasRender = () => {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve();
      });
    });
  });
};

/**
 * Creates a new image payload object
 * @param {string} type - Image type
 * @param {string} url - Image URL
 * @returns {Object} Image payload
 */
export const getNewImagePayload = ({ type, url, fileName }) => {
  const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf(".")) || fileName;

  return {
    url,
    type,
    graphicTags: [],
    graphicTitle: `${nameWithoutExt}-${Date.now()}`,
  };
};

export const extractGraphicData = (image) => {
  const _id = image?._id || {};

  return {
    _id: _id?._id,
    graphicTags: _id?.graphicTags,
    contentType: _id?.contentType,
    type: _id?.graphicType,
    url: _id?.imgUrl,
    graphicTitle: _id?.graphicTitle,
  };
};

/**
 * Helper function to update the parent group of an object
 * This is needed when the size of a child object changes
 *
 * @param {string} path - Object path
 * @param {Object} editorInstance - Editor instance
 */
export const updateParentGroup = (path, editorInstance) => {
  const pathParts = path.split(".");
  // object[9].objects[10].objects[11]

  for (let i = pathParts.length - 1; i > 0; i--) {
    const parentPath = pathParts.slice(0, i).join(".");
    if (!parentPath || parentPath === "objects") continue;

    const parentGroup = findObjectByPath(editorInstance.canvas, parentPath);
    if (parentGroup && parentGroup.type === "group") {
      // Store original position
      const originalLeft = parentGroup.left;
      const originalTop = parentGroup.top;

      // addWithUpdate recalculates group dimensions and forces Fabric to update internal elements
      parentGroup.addWithUpdate();

      // Restore original position (addWithUpdate might shift it)
      parentGroup.set({
        left: originalLeft,
        top: originalTop,
      });

      parentGroup.setCoords();
      editorInstance.canvas.requestRenderAll();
    }
  }
};
